'use client';

import { cva } from 'class-variance-authority';
import classNames from 'classnames';
import React from 'react';

import { BaseUIComponentProps, defaultBaseUIComponentProps } from './base';
import { useGlobalDefaults } from './provider';

export type HeadingVariant = 'title' | 'page-title';

type HeadingProps<E extends React.ElementType = 'h2'> = BaseUIComponentProps & {
    children: React.ReactNode;
    variant?: HeadingVariant;
    id?: string;
    as?: E;
} & Omit<React.ComponentPropsWithoutRef<E>, 'as' | 'children' | 'className' | 'id'>;

const resolveDefaultAs = (variant: HeadingProps['variant']) => {
    switch (variant) {
        default:
        case 'title':
            return 'h2';
        case 'page-title':
            return 'h1';
    }
};

const headingVariants = cva('font-recoleta', {
    variants: {
        mode: {
            dark: 'text-white',
            light: 'text-black',
        },
        variant: {
            title: 'text-2xl font-normal',
            'page-title': 'text-4xl font-normal',
        },
    },

    defaultVariants: {
        variant: 'title',
        mode: defaultBaseUIComponentProps.mode,
    },
});

export function Heading<E extends React.ElementType = 'h2'>({
    children,
    variant = 'title',
    mode,
    as,
    id,
    className,
    ...rest
}: HeadingProps<E>) {
    const Component = (as ?? resolveDefaultAs(variant)) as React.ElementType;

    const defaults = useGlobalDefaults(defaultBaseUIComponentProps);

    return (
        <Component
            className={classNames(
                headingVariants({
                    mode: mode ?? defaults.mode,

                    variant,
                }),
                className
            )}
            id={id}
            {...rest}
        >
            {children}
        </Component>
    );
}
